import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { useEditCodeMutation } from "@modules/codes/services/mutations";
import { CodeFormValues, codeSchema } from "@modules/codes/validations";
import { listCodesQueryKey } from "@modules/codes/services/queries/listCodes/query";

export function useEditCodeModal(modalRef: RefObject<ModalRef>, onComplete?: () => void) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<CodeFormValues>({
    resolver: zodResolver(codeSchema()),
    mode: "onBlur",
  });

  const { mutateAsync, isPending } = useEditCodeMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listCodesQueryKey,
        refetchType: 'all',
      });

      open?.({
        type: "success",
        message: translate("limited_code_admin.success.edit"),
      });

      onComplete?.();
      modalRef.current?.close();
    },
  });

  const handleSubmit = async (data: CodeFormValues) => {
    const codeId = modalRef.current?.getId();
    await mutateAsync({ id: codeId as string, data });
  }

  return {
    methods,
    isPending,
    handleSubmit,
  };
}
